import { ContentCopy, Inventory } from "@mui/icons-material";
import clsx from "clsx";
import { FC, MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { AccessControl, MuiButton } from "@ryde/controls";
import { useModalConfirm } from "@ryde/redux";
import {
  ConfirmResult,
  isInnerItemEditingAtom,
  RydeUiUnit,
} from "@ryde/shared";

import { EditFormFooterButton } from "../../../right";
import { CrudEditFormFooterStyled } from "./crud-edit-form-footer.styled";
import { ExpandEditFormFooterFlow } from "./expand-edit-form-footer";

interface ICrudEditFormFooterProps {
  onSaveClick?: () => void;
  onCancelClick: () => void;
  isLoading?: boolean;
  saveLabel?: string;
  isSaveDisabled?: boolean;
  isInnerItemEditing?: boolean;
  cancelLabel?: string;
  entity: RydeUiUnit;
  archiveClick?: MouseEventHandler<HTMLElement>;
  duplicateClick?: MouseEventHandler<HTMLElement>;
  deleteMessage?: string;
  duplicationMessage?: string;
  isArchiveDisabled?: boolean;
  isDuplicateDisabled?: boolean;
}

export const CrudEditFormFooter: FC<ICrudEditFormFooterProps> = ({
  onSaveClick,
  onCancelClick,
  archiveClick,
  duplicateClick,
  isLoading = false,
  saveLabel,
  isSaveDisabled = false,
  isInnerItemEditing: isInnerItemEditingProp,
  cancelLabel,
  entity,
  deleteMessage,
  duplicationMessage,
  isDuplicateDisabled,
  isArchiveDisabled,
}) => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const isInnerItemEditingFromAtom = useRecoilValue(isInnerItemEditingAtom);
  const isInnerItemEditing =
    isInnerItemEditingProp ?? isInnerItemEditingFromAtom;

  const archiveMessage = deleteMessage || t(`general.ITEM_ARCHIVE_MESSAGE`);
  const dupMessage = duplicationMessage || t(`general.ITEM_DUPLICATE_MESSAGE`);

  const onArchiveClickEvent = useEvent(
    async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e.preventDefault();

      if (archiveClick) {
        const confirmationResult = await confirmDialogResult({
          title: t("general.ARCHIVE_CONFIRM"),
          message: archiveMessage,
        });

        if (confirmationResult === ConfirmResult.OK) {
          archiveClick(e);
        }
      }
    },
  );

  const onDuplicateClickEvent = useEvent(
    async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e.preventDefault();

      if (duplicateClick) {
        const confirmationResult = await confirmDialogResult({
          title: t("general.DUPLICATE_CONFIRM"),
          message: dupMessage,
        });

        if (confirmationResult === ConfirmResult.OK) {
          duplicateClick(e);
        }
      }
    },
  );
  return (
    <CrudEditFormFooterStyled
      className={clsx(isInnerItemEditing && "inner-item-editing")}
    >
      {archiveClick && (
        <AccessControl entity={entity || "unknown"} action="delete">
          <EditFormFooterButton
            onClick={onArchiveClickEvent}
            disabled={isArchiveDisabled || isLoading}
          >
            <Inventory style={{ fontSize: "16px" }} />
            <span>{t(`general.ARCHIVE`)}</span>
          </EditFormFooterButton>
        </AccessControl>
      )}
      {duplicateClick && (
        <AccessControl entity={entity || "unknown"} action="create">
          <EditFormFooterButton
            onClick={onDuplicateClickEvent}
            disabled={isDuplicateDisabled || isLoading}
          >
            <ContentCopy style={{ fontSize: "16px" }} />
            <span>{t(`general.DUPLICATE`)}</span>
          </EditFormFooterButton>
        </AccessControl>
      )}
      <div className="cancel_button">
        <ExpandEditFormFooterFlow entity={entity} onSaveClick={onSaveClick} />
        <MuiButton
          e2eData={`cancel_button_right_footer`}
          muiVariant="outlined"
          color="secondary"
          onClick={onCancelClick}
          loading={isLoading}
        >
          {cancelLabel || t(`general.CANCEL`)}
        </MuiButton>
        {Boolean(onSaveClick) && (
          <MuiButton
            e2eData={`save_button_right_footer`}
            loading={isLoading}
            muiStyle={
              isSaveDisabled || isInnerItemEditing ? "error" : "primary"
            }
            disabled={isSaveDisabled || isInnerItemEditing}
            onClick={onSaveClick}
          >
            {saveLabel || t(`general.SAVE`)}
          </MuiButton>
        )}
      </div>
    </CrudEditFormFooterStyled>
  );
};
